import { motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";

import {
  REVEAL_DURATION,
  REVEAL_EASE_OUT,
  REVEAL_Y,
} from "@/app/common/functions/motion/config";
import { resolveRevealAnimate } from "@/app/common/functions/motion/revealAnimation";
import { useReveal } from "@/app/common/hooks/useReveal";

export type RevealProps = {
  children: ReactNode;
  /** Seconds to wait after it scrolls into view. */
  delay?: number;
  /** How far below its place it starts, in pixels. */
  y?: number;
};

/**
 * Fades its children up into place once they scroll into view. A wrapper with
 * no look of its own: the layout is the child's.
 */
export function Reveal({ children, delay = 0, y = REVEAL_Y }: RevealProps) {
  const shouldReduceMotion = useReducedMotion();
  const [ref, hasRevealed] = useReveal();

  return (
    <motion.div
      ref={ref}
      initial={shouldReduceMotion ? false : { opacity: 0, y }}
      animate={resolveRevealAnimate(shouldReduceMotion, hasRevealed, y)}
      transition={
        shouldReduceMotion
          ? { duration: 0 }
          : { duration: REVEAL_DURATION, delay, ease: REVEAL_EASE_OUT }
      }
    >
      {children}
    </motion.div>
  );
}
